import { capitalize } from 'lodash';
import { Droplet, PlayCircle } from 'lucide-react';
import Image from 'next/image';
import { useMemo, useState } from 'react';

import { SimpleModal } from '@/components/modal';
import { Avatar, AvatarFallback, Tabs, TabsList, TabsTrigger, Text } from '@/design-system/components/ui';

import { EventTriggerType } from '../domain';
import { usePendingTriggersStore } from '../stores/pendingTriggersStore';
import { UserIdentifierProperty } from '../types';
import { TriggerTypeSearchBar } from './TriggerTypeSearchBar';

interface Props {
  open: boolean;
  onClose: (option: SelectTriggerOption | undefined) => void;
}

export enum TriggerCategories {
  ALL = 'all',
  HOLDING = 'holding',
  LIQUIDITY = 'liquidity',
  LENDING = 'lending',
  TRADING = 'trading',
  QUESTS = 'quests',
  YIELD = 'yield',
  CUSTOM = 'custom',
}

export interface SelectTriggerOption {
  description: string;
  imageSrc: string | React.ReactNode;
  title: string;
  triggerType: EventTriggerType;
  userIdentifierProperty: UserIdentifierProperty;
  category: TriggerCategories;
}

export const triggerOptions: SelectTriggerOption[] = [
  {
    description: 'Create a trigger from an onchain function call or event in your smart contract',
    imageSrc: '/assets/png/blockchain.png',
    title: 'Custom Onchain',
    triggerType: EventTriggerType.OnChain,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.CUSTOM,
  },
  {
    description: 'Create a trigger from a custom event sent through the Fuul API',
    imageSrc: '/assets/png/coding.png',
    title: 'API',
    triggerType: EventTriggerType.CustomEvent,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.CUSTOM,
  },
  {
    description: 'Create a trigger from snapshots of Uniswap v3 liquidity pool positions',
    imageSrc: '/assets/png/uniswap.png',
    title: 'Uniswap v3 Liquidity',
    triggerType: EventTriggerType.Uniswap,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LIQUIDITY,
  },
  {
    description: 'Create a trigger from snapshots of Uniswap v4 liquidity pool positions',
    imageSrc: '/assets/png/uniswap.png',
    title: 'Uniswap v4 Liquidity',
    triggerType: EventTriggerType.UniswapV4,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LIQUIDITY,
  },
  {
    description: 'Create a trigger based on the results of a Dune query',
    imageSrc: '/assets/svg/dune.svg',
    title: 'Custom Dune Query',
    triggerType: EventTriggerType.Dune,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.CUSTOM,
  },
  {
    description: 'Create a trigger from snapshots of users holding a certain token',
    imageSrc: '/assets/svg/ethereum.svg',
    title: 'EVM Token Holders',
    triggerType: EventTriggerType.OnChainTokenHolder,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.HOLDING,
  },
  {
    description: 'Create a trigger from snapshots of SUI token holders',
    imageSrc: '/assets/webp/sui.webp',
    title: 'SUI Token Holders',
    triggerType: EventTriggerType.SuiTokenHolders,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.HOLDING,
  },
  {
    description: 'Create a trigger from snapshots of Solana token holders',
    imageSrc: '/assets/webp/solana.webp',
    title: 'Solana Token Holders',
    triggerType: EventTriggerType.SolanaTokenHolders,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.HOLDING,
  },
  {
    description: 'Create a trigger from snapshots of Fogo token holders',
    imageSrc: '/assets/webp/fogo.webp',
    title: 'Fogo Token Holders',
    triggerType: EventTriggerType.FogoTokenHolders,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.HOLDING,
  },
  {
    description: 'Create a trigger from snapshots of Stellar token holders',
    imageSrc: '/assets/webp/stellar.webp',
    title: 'Stellar Token Holders',
    triggerType: EventTriggerType.StellarTokenHolders,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.HOLDING,
  },
  {
    description: 'Create a trigger from snapshots of Ignition locked iFOGO positions',
    imageSrc: '/assets/webp/fogo.webp',
    title: 'Ignition Locked iFOGO',
    triggerType: EventTriggerType.FogoIgnitionLocked,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.HOLDING,
  },
  {
    description: 'Create a trigger from snapshots of v2 liquidity pool positions',
    imageSrc: <Droplet color="#4C6EF5" size={32} />,
    title: 'V2 Liquidity',
    triggerType: EventTriggerType.OnChainV2Liquidity,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LIQUIDITY,
  },
  {
    description: 'Create a Lynx Trade trigger',
    imageSrc: '/assets/svg/lynx.svg',
    title: 'Lynx Trade',
    triggerType: EventTriggerType.LynxTrade,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.TRADING,
  },
  {
    description: 'Create a trigger from Nado trades',
    imageSrc: '/assets/webp/nado.webp',
    title: 'Nado',
    triggerType: EventTriggerType.NadoTrade,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.TRADING,
  },
  {
    description: 'Create a trigger from Nado Purps mobile app trades',
    imageSrc: '/assets/webp/nado.webp',
    title: 'Nado Purps (mobile)',
    triggerType: EventTriggerType.NadoPurpsTrade,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.TRADING,
  },
  {
    description: 'Create a Hibachi Trade trigger',
    imageSrc: '/assets/webp/hibachi.webp',
    title: 'Hibachi Trade',
    triggerType: EventTriggerType.HibachiTrading,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.TRADING,
  },
  {
    description: 'Create a trigger for Hibachi multilevel affiliate program fees',
    imageSrc: '/assets/webp/hibachi.webp',
    title: 'Hibachi Affiliates',
    triggerType: EventTriggerType.HibachiAffiliates,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.TRADING,
  },
  {
    description: 'Create a trigger for Ambient trades',
    imageSrc: '/assets/webp/ambient.webp',
    title: 'Ambient Trade',
    triggerType: EventTriggerType.AmbientTrade,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.TRADING,
  },
  {
    description: 'Create a trigger from snapshots of users balances from a subgraph',
    imageSrc: '/assets/svg/graph.svg',
    title: 'Subgraph balances',
    triggerType: EventTriggerType.OnChainSubgraphEvent,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.CUSTOM,
  },
  {
    description: 'Create a trigger for Morpho Vault performance fees',
    imageSrc: '/assets/svg/morpho.svg',
    title: 'Morpho Vaults - Performance Fee',
    triggerType: EventTriggerType.Morpho,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LENDING,
  },
  {
    description: 'Create a trigger from deposits on Morpho Vaults',
    imageSrc: '/assets/svg/morpho.svg',
    title: 'Deposit on Morpho Vaults',
    triggerType: EventTriggerType.MorphoVaults,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LENDING,
  },
  {
    description: 'Create a trigger from snapshots of Morpho market positions (borrow, collateral, or supply)',
    imageSrc: '/assets/svg/morpho.svg',
    title: 'Morpho Market Strategies',
    triggerType: EventTriggerType.MorphoBorrowerStrategy,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LENDING,
  },
  {
    description: 'Create a trigger from deposits on Euler Vaults',
    imageSrc: '/assets/webp/euler.webp',
    title: 'Deposit on Euler Vaults',
    triggerType: EventTriggerType.EulerVaults,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LENDING,
  },
  {
    description: 'Create a trigger for Euler V2 looping net deposits',
    imageSrc: '/assets/webp/euler.webp',
    title: 'Euler V2 Looping - Net Deposit',
    triggerType: EventTriggerType.EulerV2Looping,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LENDING,
  },
  {
    description: 'Create a trigger from snapshots of Hypurr Euler net positions',
    imageSrc: '/assets/svg/hypurr.svg',
    title: 'Hypurr Euler Net Positions',
    triggerType: EventTriggerType.HypurrEulerNetPositions,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LENDING,
  },
  {
    description: 'Create a trigger from snapshots of Compound v3 lending or borrowing positions',
    imageSrc: '/assets/svg/compound.svg',
    title: 'Compound v3 position',
    triggerType: EventTriggerType.CompoundV3,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LENDING,
  },
  {
    description: 'Create a trigger from snapshots of Hypurr net pooled positions',
    imageSrc: '/assets/svg/hypurr.svg',
    title: 'Hypurr Net pooled positions',
    triggerType: EventTriggerType.HypurrNetPoolPositions,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LENDING,
  },
  {
    description: 'Create a trigger from snapshots of Aquarius liquidity positions',
    imageSrc: '/assets/webp/stellar.webp',
    title: 'Aquarius Liquidity',
    triggerType: EventTriggerType.AquariusLiquidity,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LIQUIDITY,
  },
  {
    description: 'Create a trigger for swaps on an Aquarius pool',
    imageSrc: '/assets/webp/stellar.webp',
    title: 'Aquarius Swaps',
    triggerType: EventTriggerType.AquariusSwaps,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.TRADING,
  },
  {
    description: 'Create a trigger from snapshots of Blend lending or borrowing positions',
    imageSrc: '/assets/webp/stellar.webp',
    title: 'Blend',
    triggerType: EventTriggerType.BlendPositions,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LENDING,
  },
  {
    description: 'Create a trigger from snapshots of Pyron lending or borrowing positions',
    imageSrc: '/assets/webp/pyron.webp',
    title: 'Pyron',
    triggerType: EventTriggerType.Pyron,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LENDING,
  },
  {
    description: 'Create a trigger from snapshots of FogoLend deposit or borrow positions',
    imageSrc: '/assets/webp/fogo.webp',
    title: 'FogoLend',
    triggerType: EventTriggerType.FogoLend,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LENDING,
  },
  {
    description: 'Create a trigger from snapshots of Felix Vanilla Collateral positions',
    imageSrc: '/assets/webp/felix-trigger.webp',
    title: 'Felix Vanilla Collateral',
    triggerType: EventTriggerType.FelixVanillaCollateral,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LENDING,
  },
  {
    description: 'Create a trigger from snapshots of Felix CDP Collateral positions',
    imageSrc: '/assets/webp/felix-trigger.webp',
    title: 'Felix CDP Collateral',
    triggerType: EventTriggerType.FelixCdpCollateral,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LENDING,
  },
  {
    description: 'Create a trigger from Gearbox protocol positions (Total Position or Supply)',
    imageSrc: '/assets/webp/gearbox.webp',
    title: 'Gearbox',
    triggerType: EventTriggerType.Gearbox,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LENDING,
  },
  {
    description: "Create a trigger from user's follow activity on X",
    imageSrc: '/assets/svg/x.svg',
    title: 'Follow on X',
    triggerType: EventTriggerType.XFollowEvent,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.QUESTS,
  },
  {
    description: "Create a trigger from user's post activity on X",
    imageSrc: '/assets/svg/x.svg',
    title: 'Post on X',
    triggerType: EventTriggerType.XPostEvent,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.QUESTS,
  },
  {
    description: "Create a trigger from a user's repost on X",
    imageSrc: '/assets/svg/x.svg',
    title: 'Repost on X',
    triggerType: EventTriggerType.XRepostEvent,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.QUESTS,
  },
  {
    description: "Create a trigger from a user's like on X",
    imageSrc: '/assets/svg/x.svg',
    title: 'Like on X',
    triggerType: EventTriggerType.XLikeEvent,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.QUESTS,
  },
  {
    description: "Create a trigger from user's activity on Github",
    imageSrc: '/assets/png/github.png',
    title: 'Github Activity',
    triggerType: EventTriggerType.Github,
    userIdentifierProperty: UserIdentifierProperty.GithubUsername,
    category: TriggerCategories.QUESTS,
  },
  {
    description: "Create a trigger from user's activity on Discord",
    imageSrc: '/assets/png/discord.png',
    title: 'Discord Activity',
    triggerType: EventTriggerType.Discord,
    userIdentifierProperty: UserIdentifierProperty.DiscordUsername,
    category: TriggerCategories.QUESTS,
  },
  {
    description: 'Create a trigger when a user joins your Discord server',
    imageSrc: '/assets/png/discord.png',
    title: 'Discord Server Join',
    triggerType: EventTriggerType.DiscordServerJoin,
    userIdentifierProperty: UserIdentifierProperty.DiscordUsername,
    category: TriggerCategories.QUESTS,
  },
  {
    description: 'Create a trigger when a user is assigned a specific Discord role',
    imageSrc: '/assets/png/discord.png',
    title: 'Discord Role Assigned',
    triggerType: EventTriggerType.DiscordRoleAssigned,
    userIdentifierProperty: UserIdentifierProperty.DiscordUsername,
    category: TriggerCategories.QUESTS,
  },
  {
    description: 'Track Stripe payments from your connected account (one trigger per project)',
    imageSrc: '/assets/svg/stripe.svg',
    title: 'Stripe Payment',
    triggerType: EventTriggerType.StripePayment,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.CUSTOM,
  },
  {
    title: 'Zealy Quest',
    description: 'Create a trigger from a Zealy quest',
    imageSrc: '/assets/svg/zealy.svg',
    triggerType: EventTriggerType.Zealy,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.QUESTS,
  },
  {
    title: 'Galxe Quest',
    description: 'Create a trigger from a Galxe quest',
    imageSrc: '/assets/svg/galxe.svg',
    triggerType: EventTriggerType.Galxe,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.QUESTS,
  },
  {
    title: 'Pendle YT',
    description: 'Create a trigger from Pendle YT holding.',
    imageSrc: '/assets/svg/pendle.svg',
    triggerType: EventTriggerType.Pendle,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.YIELD,
  },
  {
    title: 'Pendle LP',
    description: 'Create a trigger from Pendle LP holding with liquid locker support.',
    imageSrc: '/assets/svg/pendle.svg',
    triggerType: EventTriggerType.PendleLP,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.YIELD,
  },
  {
    title: 'Spectra YT',
    description: 'Create a trigger from Spectra Yield Token holding.',
    imageSrc: '/assets/svg/spectra.svg',
    triggerType: EventTriggerType.SpectraYT,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.YIELD,
  },
  {
    title: 'Spectra LP',
    description: 'Create a trigger from Spectra LP Token holding.',
    imageSrc: '/assets/svg/spectra.svg',
    triggerType: EventTriggerType.SpectraLP,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.YIELD,
  },
  {
    title: 'Exponent',
    description: 'Create a trigger from snapshots of Exponent YT, orderbook or CLMM positions',
    imageSrc: '/assets/webp/exponent.webp',
    triggerType: EventTriggerType.Exponent,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.YIELD,
  },
  {
    description: 'Create a trigger from snapshots of Balancer LP token holders',
    imageSrc: '/assets/svg/balancer.svg',
    title: 'Balancer LP Holders',
    triggerType: EventTriggerType.BalancerPool,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.HOLDING,
  },
  {
    description: 'Create a trigger from HypurrFi card transactions',
    imageSrc: '/assets/svg/hypurr.svg',
    title: 'HypurrFi Card Transaction',
    triggerType: EventTriggerType.HypurrCardTransactions,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.TRADING,
  },
  {
    description: 'Create a trigger from snapshots of HyperSwap v3 liquidity pool positions',
    imageSrc: '/assets/svg/hyperswap.svg',
    title: 'HyperSwap v3 Liquidity',
    triggerType: EventTriggerType.HyperswapLiquidityV3,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LIQUIDITY,
  },
  {
    description: 'Create a trigger from snapshots of Hybra v3 liquidity pool positions',
    imageSrc: '/assets/png/hybra.png',
    title: 'Hybra Liquidity',
    triggerType: EventTriggerType.HybraLiquidityV3,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LIQUIDITY,
  },
  {
    description: 'Create a trigger for Orderly Builder Trades',
    imageSrc: '/assets/webp/orderly.webp',
    title: 'Orderly Builder Trades',
    triggerType: EventTriggerType.OrderlyBuilderTrades,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.TRADING,
  },
  {
    description: 'Create a trigger for Hyperliquid builder trades',
    imageSrc: '/assets/webp/hyperliquid.webp',
    title: 'Hyperliquid Trade',
    triggerType: EventTriggerType.HyperliquidTrade,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.TRADING,
  },
  {
    description: 'Create a trigger for spot trades on Valiant protocol',
    imageSrc: '/assets/webp/valiant.webp',
    title: 'Trade spot on Valiant',
    triggerType: EventTriggerType.ValiantTrade,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.TRADING,
  },
  {
    description: 'Create a trigger for liquidity provision on Valiant protocol',
    imageSrc: '/assets/webp/valiant.webp',
    title: 'Valiant Liquidity',
    triggerType: EventTriggerType.ValiantLiquidity,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LIQUIDITY,
  },
  {
    description: 'Create a trigger from snapshots of Prj X v3 liquidity pool positions',
    imageSrc: '/assets/webp/prjx.webp',
    title: 'Prj X v3 Liquidity',
    triggerType: EventTriggerType.PrjXLiquidityV3,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LIQUIDITY,
  },
  {
    description: 'Create a trigger from snapshots of KittenSwap v3 liquidity pool positions',
    imageSrc: '/assets/webp/kittenswap.webp',
    title: 'KittenSwap v3 Liquidity',
    triggerType: EventTriggerType.KittenSwapLiquidityV3,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LIQUIDITY,
  },
  {
    description: 'Create a trigger from snapshots of Ultrasolid v3 liquidity pool positions',
    imageSrc: '/assets/webp/ultrasolid.webp',
    title: 'Ultrasolid v3 Liquidity',
    triggerType: EventTriggerType.UltrasolidLiquidityV3,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LIQUIDITY,
  },
  {
    description: 'Create a trigger from snapshots of Upheaval v3 liquidity pool positions',
    imageSrc: '/assets/webp/upheaval.webp',
    title: 'Upheaval v3 Liquidity',
    triggerType: EventTriggerType.UpheavalLiquidityV3,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LIQUIDITY,
  },
  {
    description: 'Create a trigger from snapshots of Nest v3 liquidity pool positions',
    imageSrc: '/assets/webp/nest.webp',
    title: 'Nest v3 Liquidity',
    triggerType: EventTriggerType.NestLiquidityV3,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.LIQUIDITY,
  },
  {
    description: 'Create a trigger to track on-chain trade volume (USD) for a Polymarket market',
    imageSrc: '/assets/webp/polymarket.webp',
    title: 'Polymarket Trade Volume',
    triggerType: EventTriggerType.PolymarketTradeVolume,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.TRADING,
  },
  {
    description: 'Create a trigger for Pacifica Builder Trades',
    imageSrc: '/assets/webp/pacifica.webp',
    title: 'Pacifica Trades',
    triggerType: EventTriggerType.PacificaBuilderTrades,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.TRADING,
  },
  {
    description: 'Track builder trade volume and revenue per Odyssey Finance market',
    imageSrc: '/assets/svg/odyssey.svg',
    title: 'Odyssey Builder Trades',
    triggerType: EventTriggerType.OdysseyBuilderTrades,
    userIdentifierProperty: UserIdentifierProperty.UserAddress,
    category: TriggerCategories.TRADING,
  },
];

/** Trigger types shown by default; everything else sits behind the crypto/quest switch. */
export const DEFAULT_TRIGGER_TYPES: EventTriggerType[] = [
  EventTriggerType.CustomEvent, // "API" — the Fuul API trigger
  EventTriggerType.StripePayment,
];

export default function TriggerTypeSelectorModal({ open, onClose }: Props) {
  const [selectedTab, setSelectedTab] = useState(TriggerCategories.ALL);
  const [searchQuery, setSearchQuery] = useState('');
  const pendingTriggers = usePendingTriggersStore((s) => s.pendingTriggers);

  const filteredOptions = useMemo(() => {
    const hasStripePaymentPending = pendingTriggers.some((t) => t.triggerType === EventTriggerType.StripePayment);

    const filteredByTabs = triggerOptions.filter(
      (option) => selectedTab === TriggerCategories.ALL || option.category === selectedTab,
    );
    return filteredByTabs
      .filter(
        (option) =>
          option.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          option.description.toLowerCase().includes(searchQuery.toLowerCase()),
      )
      .filter((option) => {
        if (option.triggerType === EventTriggerType.StripePayment && hasStripePaymentPending) {
          return false;
        }
        return true;
      });
  }, [triggerOptions, selectedTab, searchQuery, pendingTriggers]);

  return (
    <SimpleModal
      open={open}
      onClose={() => onClose(undefined)}
      title={
        <div className="tw:flex tw:flex-row tw:items-center tw:gap-3 tw:pt-4">
          <Avatar size="lg">
            <AvatarFallback>
              <PlayCircle size={24} />
            </AvatarFallback>
          </Avatar>
          <div className="tw:flex tw:flex-col tw:gap-1">
            <Text variant="title-sm" as="h2">
              Create trigger
            </Text>
            <Text variant="body-sm" color="muted">
              Select the type of trigger to create
            </Text>
          </div>
        </div>
      }
      className="tw:min-w-[90vw] tw:md:min-w-[800px] tw:h-[80vh] tw:max-h-[800px]"
    >
      <div className="tw:flex tw:flex-col tw:gap-4">
        <div className="tw:w-full tw:sticky tw:top-0 tw:z-[2] tw:flex tw:flex-col tw:gap-4 tw:py-2 tw:bg-card">
          <TriggerTypeSearchBar value={searchQuery} onChange={setSearchQuery} />
          <Tabs value={selectedTab} onValueChange={(v) => setSelectedTab(v as TriggerCategories)}>
            <TabsList className="tw:border-b tw:border-border tw:w-full">
              {Object.values(TriggerCategories).map((category) => (
                <TabsTrigger key={category} value={category}>
                  {capitalize(category)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        <div className="tw:flex-1 tw:overflow-y-auto tw:overflow-x-visible tw:w-full tw:p-[0.3rem]">
          {filteredOptions.length > 0 ? (
            <div className="tw:flex tw:flex-col tw:gap-6">
              {filteredOptions.map((option) => (
                <div
                  key={option.title}
                  className="tw:w-full tw:rounded-lg tw:p-4 tw:cursor-pointer tw:border tw:border-border tw:bg-card tw:shadow-sm tw:transition-[background] tw:duration-200 tw:hover:bg-indigo-500/[0.08]"
                  onClick={() => onClose(option)}
                >
                  <div className="tw:flex tw:flex-row tw:gap-4 tw:items-center">
                    {typeof option.imageSrc === 'string' ? (
                      <Image alt={option.title} width={32} height={32} src={option.imageSrc} />
                    ) : (
                      option.imageSrc
                    )}
                    <div className="tw:text-left">
                      <Text variant="body-sm" weight="bold">
                        {option.title}
                      </Text>
                      <Text variant="body-sm" color="muted">
                        {option.description}
                      </Text>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Text variant="body-sm" color="muted" className="tw:text-center tw:mt-4">
              No triggers found
            </Text>
          )}
        </div>
      </div>
    </SimpleModal>
  );
}
